import React, { useState } from 'react';
import { DocumentSection } from '../../../core/ast/document.types';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  Copy,
  Trash2,
  FolderTree,
  FileCode,
} from 'lucide-react';
import { Button } from '../../../shared/components/Button';

interface ReportOutlineNavProps {
  sections: DocumentSection[];
  onSelectSection: (sectionId: string) => void;
  onAddSection: (title: string, type?: DocumentSection['type']) => void;
  onMoveSection: (sectionId: string, direction: 'up' | 'down') => void;
  onDuplicateSection: (sectionId: string) => void;
  onToggleEnabled: (sectionId: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onToggleCollapseAll: (collapse: boolean) => void;
  onOpenAddCoverModal: () => void;
}

const PRESET_SECTION_SUGGESTIONS = [
  'Objectives',
  'Introduction',
  'Theoretical Background',
  'Apparatus & Instruments',
  'Circuit Schematic',
  'Experimental Procedure',
  'Data & Measurements',
  'Sample Calculations',
  'Error Analysis & Uncertainty',
  'Results & Graphs',
  'Discussion',
  'Viva Voce Questions',
  'Conclusion',
  'References',
  'Appendix',
];

export const ReportOutlineNav: React.FC<ReportOutlineNavProps> = ({
  sections,
  onSelectSection,
  onAddSection,
  onMoveSection,
  onDuplicateSection,
  onToggleEnabled,
  onDeleteSection,
  onToggleCollapseAll,
  onOpenAddCoverModal,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredSections = sections.filter((sec) =>
    sec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sec.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customTitle.trim()) return;
    onAddSection(customTitle.trim(), 'custom');
    setCustomTitle('');
    setShowAddMenu(false);
  };

  const handlePickPreset = (presetName: string) => {
    onAddSection(presetName, 'custom');
    setShowAddMenu(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'var(--bg-surface)',
        borderRight: '1px solid var(--border-subtle)',
        width: '280px',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {/* Outline Header */}
      <div
        style={{
          padding: 'var(--space-3)',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--text-tertiary)',
              letterSpacing: '0.05em',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <FolderTree size={13} color="var(--primary-600)" />
            Report Structure ({sections.length})
          </span>
          <div style={{ display: 'flex', gap: 2 }}>
            <button
              type="button"
              className="icon-btn"
              style={{ width: 22, height: 22 }}
              onClick={() => onToggleCollapseAll(false)}
              title="Expand All Sections"
            >
              <ChevronDown size={12} />
            </button>
            <button
              type="button"
              className="icon-btn"
              style={{ width: 22, height: 22 }}
              onClick={() => onToggleCollapseAll(true)}
              title="Collapse All Sections"
            >
              <ChevronRight size={12} />
            </button>
          </div>
        </div>

        {/* Search Input */}
        <div style={{ position: 'relative' }}>
          <Search
            size={12}
            color="var(--text-muted)"
            style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)' }}
          />
          <input
            type="text"
            className="form-input text-xs"
            style={{ paddingLeft: '26px', height: '28px' }}
            placeholder="Filter sections... (Ctrl+K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Quick Add Buttons */}
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          <Button
            variant="secondary"
            size="sm"
            style={{ flex: 1, fontSize: '11px', padding: '4px' }}
            icon={<Plus size={12} />}
            onClick={() => setShowAddMenu(!showAddMenu)}
          >
            Add Section
          </Button>
          <Button
            variant="ghost"
            size="sm"
            style={{ fontSize: '11px', padding: '4px 6px' }}
            icon={<FileCode size={12} />}
            onClick={onOpenAddCoverModal}
            title="Cover Page"
          >
            Cover Page
          </Button>
        </div>
      </div>

      {/* Add Section Dropdown Palette */}
      {showAddMenu && (
        <div
          style={{
            padding: 'var(--space-3)',
            backgroundColor: 'var(--bg-surface-subtle)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            maxHeight: '220px',
            overflowY: 'auto',
          }}
        >
          <form onSubmit={handleCreateCustom} style={{ display: 'flex', gap: 4 }}>
            <input
              type="text"
              className="form-input text-xs"
              placeholder="Custom Section Name..."
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              autoFocus
            />
            <Button type="submit" variant="primary" size="sm" style={{ padding: '2px 8px' }}>
              Add
            </Button>
          </form>

          <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)' }}>
            Suggested Lab Sections:
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {PRESET_SECTION_SUGGESTIONS.map((preset, i) => (
              <button
                key={i}
                type="button"
                className="btn btn-ghost btn-sm text-xs"
                style={{ fontSize: '10px', padding: '2px 6px', border: '1px solid var(--border-subtle)' }}
                onClick={() => handlePickPreset(preset)}
              >
                + {preset}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sections List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--space-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        {filteredSections.map((sec, idx) => {
          const isDeleting = confirmDeleteId === sec.id;
          return (
            <div
              key={sec.id}
              onClick={() => onSelectSection(sec.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: sec.enabled ? 'var(--bg-surface)' : 'var(--bg-surface-subtle)',
                border: '1px solid var(--border-subtle)',
                opacity: sec.enabled ? 1 : 0.6,
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', width: '16px' }}>
                  {idx + 1}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 500,
                    color: sec.enabled ? 'var(--text-primary)' : 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={sec.title}
                >
                  {sec.title}
                </span>
                <span
                  style={{
                    fontSize: '9px',
                    padding: '1px 4px',
                    borderRadius: 'var(--radius-xs)',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {sec.blocks.length}
                </span>
              </div>

              {/* Hoverable / Compact Action Buttons */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 20, height: 20 }}
                  disabled={idx === 0}
                  onClick={() => onMoveSection(sec.id, 'up')}
                  title="Move Up"
                >
                  <ArrowUp size={10} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 20, height: 20 }}
                  disabled={idx === sections.length - 1}
                  onClick={() => onMoveSection(sec.id, 'down')}
                  title="Move Down"
                >
                  <ArrowDown size={10} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 20, height: 20 }}
                  onClick={() => onDuplicateSection(sec.id)}
                  title="Duplicate Section"
                >
                  <Copy size={10} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 20, height: 20 }}
                  onClick={() => onToggleEnabled(sec.id)}
                  title={sec.enabled ? 'Disable (Hide in Output)' : 'Enable'}
                >
                  {sec.enabled ? <Eye size={10} color="var(--color-success)" /> : <EyeOff size={10} />}
                </button>

                {isDeleting ? (
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm text-xs"
                      style={{ fontSize: '9px', padding: '1px 4px' }}
                      onClick={() => {
                        onDeleteSection(sec.id);
                        setConfirmDeleteId(null);
                      }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm text-xs"
                      style={{ fontSize: '9px', padding: '1px 4px' }}
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="icon-btn"
                    style={{ width: 20, height: 20 }}
                    onClick={() => setConfirmDeleteId(sec.id)}
                    title="Delete Section"
                  >
                    <Trash2 size={10} color="var(--color-danger)" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {filteredSections.length === 0 && (
          <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
            No sections match "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  );
};
